import type { CredentialProvisionResponseStatus } from "@clipboard-health/contract-backend-main";
import { Text, Title } from "@clipboard-health/ui-react";
import { isDefined } from "@clipboard-health/util-ts";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from "@mui/material";

import { APP_V2_APP_EVENTS } from "../lib";
import { logError } from "../lib/analytics";
import { ChartingCredentialSoftware } from "./api/schema";
import { useGetWorkerCredentialProvisionRequests } from "./api/useGetWorkerCredentialProvisionRequests";
import { useGetWorkerCredentials } from "./api/useGetWorkerCredentials";

const PROVISION_REQUEST_STATUS_ALERTS: Partial<
  Record<CredentialProvisionResponseStatus, { text: string; severity: "info" | "error" }>
> = {
  in_progress: {
    text: "Credential provisioning is already in progress for this worker. This process may take a few minutes.",
    severity: "info",
  },
  no_response: {
    text: "There was an error provisioning the account.",
    severity: "error",
  },
  failed: {
    text: "There was an error provisioning the account. Please try again.",
    severity: "error",
  },
};

interface ManageCredentialsModalProps {
  onClose: () => void;
  onAction: () => void;
  workerName: string | undefined;
  workerId: string;
  workplaceId: string;
}

export function ManageCredentialsModal({
  onClose,
  onAction,
  workerName,
  workerId,
  workplaceId,
}: ManageCredentialsModalProps) {
  const {
    data: workerCredentialsResponse,
    isLoading: isLoadingCredentials,
    isError: isErrorCredentials,
  } = useGetWorkerCredentials({
    filter: { workerIds: workerId, workplaceId },
  });

  const {
    data: provisionRequests,
    isLoading: isLoadingProvisionRequests,
    isError: isErrorProvisionRequests,
  } = useGetWorkerCredentialProvisionRequests({ workerId, workplaceId });
  const mostRecentRequest = provisionRequests?.data?.[0];
  const mostRecentRequestStatus = mostRecentRequest?.attributes?.status;
  const hasExistingProvisionerAccount =
    provisionRequests?.data.some((request) => request.attributes.status === "succeeded") ?? false;

  const provisionRequestAlert =
    isDefined(mostRecentRequestStatus) && PROVISION_REQUEST_STATUS_ALERTS[mostRecentRequestStatus]
      ? PROVISION_REQUEST_STATUS_ALERTS[mostRecentRequestStatus]
      : undefined;

  if (mostRecentRequestStatus === "no_response") {
    logError(APP_V2_APP_EVENTS.WORKER_CREDENTIAL_PROVISION_REQUEST_NO_RESPONSE_FAILURE, {
      error: "Worker credential provision request did not receive a response",
      metadata: {
        provisionRequestId: mostRecentRequest?.id,
      },
    });
  }

  const isMostRecentRequestInProgressOrNoResponse =
    mostRecentRequestStatus === "in_progress" || mostRecentRequestStatus === "no_response";
  const isActionDisabled = isLoadingProvisionRequests || isMostRecentRequestInProgressOrNoResponse;

  const isLoading = isLoadingCredentials || isLoadingProvisionRequests;
  const isError = isErrorCredentials || isErrorProvisionRequests;

  // The GET worker credentials endpoint doesn't appear to support filtering by software
  // Filtering can be done at the call site for now
  const pccCredential = workerCredentialsResponse?.data.find(
    (credential) =>
      credential.attributes.chartingCredentials?.software ===
      ChartingCredentialSoftware.POINT_CLICK_CARE
  );
  const chartingCredentials = pccCredential?.attributes.chartingCredentials;
  const { username, password } = chartingCredentials ?? {};

  const title = isDefined(workerName)
    ? `${ChartingCredentialSoftware.POINT_CLICK_CARE} account for ${workerName}`
    : `${ChartingCredentialSoftware.POINT_CLICK_CARE} account`;

  const actionButtonText = hasExistingProvisionerAccount ? "Reset" : "Create";

  if (isLoading) {
    return (
      <Dialog open fullWidth maxWidth="md">
        <DialogContent>
          <Box sx={{ display: "flex", justifyContent: "center", padding: 4 }}>
            <CircularProgress size={32} />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  if (isError) {
    return (
      <Dialog open fullWidth maxWidth="md">
        <DialogContent>
          <Alert severity="error">Failed to load credential information. Please try again.</Alert>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            sx={{
              paddingX: 4,
              paddingY: 1,
            }}
            onClick={() => {
              onClose();
            }}
          >
            Okay
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog
      open
      fullWidth
      maxWidth="md"
      onClose={() => {
        onClose();
      }}
    >
      <DialogTitle>
        <Stack direction="column" spacing={1}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Title component="h2" variant="h3">
              {title}
            </Title>
          </Stack>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack direction="column" spacing={3}>
          {isDefined(provisionRequestAlert) && (
            <Alert severity={provisionRequestAlert.severity}>{provisionRequestAlert.text}</Alert>
          )}
          {!isDefined(provisionRequestAlert) && !hasExistingProvisionerAccount && (
            <Alert severity="info">
              This worker currently does not have existing credentials at your workplace.
            </Alert>
          )}
          <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
            <Text semibold variant="body1">
              Username
            </Text>
            <Text variant="body2">{isDefined(username) ? username : "-"}</Text>
          </Stack>
          {/* TODO: add icon for toggling visibility like in ShareCredentialsModal */}
          <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
            <Text semibold variant="body1">
              Password
            </Text>
            <Text variant="body2">{isDefined(password) ? password : "-"}</Text>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          sx={{
            border: (theme) => `1px solid ${theme.palette.divider}`,
            color: (theme) => theme.palette.text.primary,
            paddingX: 4,
            paddingY: 1,
          }}
          disabled={isActionDisabled}
          onClick={() => {
            onAction();
          }}
        >
          {actionButtonText}
        </Button>
        <Button
          variant="contained"
          sx={{
            paddingX: 4,
            paddingY: 1,
          }}
          onClick={() => {
            onClose();
          }}
        >
          Okay
        </Button>
      </DialogActions>
    </Dialog>
  );
}
